import { useToast } from 'components/ToastManager';
import { useUpdateStripeLinkMutation } from 'hooks/internalApi';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { match, P } from 'ts-pattern';
import { useCatchMutationError } from 'utils/urql';

import { decodeBankLinkId } from '@fragment/shared-utils/ids/bankAccount';
import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { Icon } from '@fragment/ui/src/components/Icon';
import { InlineError } from '@fragment/ui/src/components/InlineError/InlineError';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { Toast } from '@fragment/ui/src/components/Toast';

type FormData = {
  rak?: string;
};

type Props = {
  workspaceId: string;
  encodedLinkId: string;
  onFormClose: VoidFunction;
};

export const UpdateStripeLinkForm = ({
  workspaceId,
  encodedLinkId,
  onFormClose,
}: Props) => {
  const { linkId } = decodeBankLinkId(encodedLinkId);
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, isValid, isDirty, errors },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      rak: '',
    },
  });

  const { showToast } = useToast();
  const { context } = useInternalApiContext();
  const [
    { error: updateStripeLinkError, data: updateStripeLinkData },
    updateStripeLink,
  ] = useCatchMutationError(useUpdateStripeLinkMutation, 'updateStripeLink');

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (data.rak) {
        const updateStripeLinkResp = await updateStripeLink(
          {
            update: {
              workspaceId,
              linkId,
              rak: data.rak,
            },
          },
          context
        );
        if (
          updateStripeLinkResp.data?.updateStripeLink.__typename ===
          'UpdateStripeLinkResult'
        ) {
          showToast(
            <Toast type="success" message="Updated Restricted Access Key." />
          );
          onFormClose();
        } else {
          showToast(
            <Toast
              type="error"
              message={`Error: (${updateStripeLinkResp.data?.updateStripeLink.code}) ${updateStripeLinkResp.data?.updateStripeLink.message}`}
            />
          );
        }
      }
      reset(data);
    },
    [
      linkId,
      reset,
      updateStripeLink,
      workspaceId,
      context,
      onFormClose,
      showToast,
    ]
  );

  const hasError = !!updateStripeLinkError || !!errors.rak;
  const onCancel = useCallback(() => {
    onFormClose();
    reset();
  }, [reset, onFormClose]);

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
    <form className="w-full space-y-f2" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <TextInput
          className="color-negative"
          label="Restricted Access Key"
          flexContainer="space-y-0"
          placeholder="New RAK"
          width="w-1/2"
          padding="p-f0"
          size="md"
          hasError={hasError}
          id="rak"
          {...register('rak', {
            required: false,
            validate: (value) => {
              if (value) {
                if (value.startsWith('rk_')) {
                  return true;
                }
                return 'Restricted Access Key must start with "rk_"';
              }
              return undefined;
            },
          })}
        />
        <span className="text-main-500">This key will not be shown again.</span>
      </div>
      <div>
        <FormButtons
          type="tertiary"
          onCancel={onCancel}
          cancelButtonLabel="Cancel"
          submitButtonLabel={isSubmitting ? 'Saving...' : 'Save Changes'}
          disabled={isSubmitting || !isValid || !isDirty}
        />
      </div>
      {(updateStripeLinkData || hasError) && (
        <div className="pt-f2">
          {updateStripeLinkData?.updateStripeLink.__typename ===
            'UpdateStripeLinkResult' && (
            <div className="flex flex-row items-center">
              <Icon type="success" backgroundColor="black" />
              &nbsp;Link updated
            </div>
          )}
          {hasError &&
            match([updateStripeLinkError, errors.rak])
              .with([P.not(undefined), P._], () => (
                <InlineError>Failed to update the link</InlineError>
              ))
              .with([P._, P.not(undefined)], ([_, formFieldErrors]) => (
                <InlineError>
                  {formFieldErrors.message ?? 'Form has errors'}
                </InlineError>
              ))
              .otherwise(() => null)}
        </div>
      )}
    </form>
  );
};
